// =============================================================================
// ui/tabs/BulkEditModal.tsx — one press, every selected trade.
// -----------------------------------------------------------------------------
// ── 🚨 THE CHECKBOX IS THE OPT-IN, AND THAT IS THE SAFETY MODEL ──
//
// Every section starts OFF. Unchecked means "leave this field alone"; checked
// reveals the input and the value is applied. That is the only thing separating
// "don't touch notes" from "set notes to empty on 63 trades", and it is why the
// row header is a checkbox rather than the input being always-live.
//
// ⚠ Each row also states what the selection currently holds — "63 of 63 currently
// untagged", "62 of 63 have no risk plan". Ported deliberately: it is what lets
// you see you are about to overwrite forty trades you already did, BEFORE the
// confirmation rather than after.
//
// ── WHAT IS NOT HERE, AND WHY IT IS SHOWN RATHER THAN DROPPED ──
//
// Confluences needs a per-trade multi-select this modal does not have. It renders
// as a disabled row that says so, because a bulk editor silently missing one of
// its ten fields looks complete and is not.
//
// ⚠ Exit Model sat disabled beside it on the claim that "the exit-model catalogue
// is not loaded by this client". The catalogue shipped with the trade form and
// this copy did not follow — the fifth time that shape has bitten (§3c). The row
// is live now, and the engine had supported it all along.
//
// **A claim about what the CLIENT can do goes stale silently. Prefer a claim
// about what THIS SURFACE does.**
// =============================================================================

import { useMemo, useState } from 'react';
import { useAccounts } from '../state/accounts';
import { usePatterns } from '../state/patterns';
import { useExitModels } from '../state/exitModels';
import { usePreferences } from '../state/preferences';
import { money, num } from '../format';
import {
  bulkOverwrites, bulkPatchFor, bulkPlanIsEmpty, emptyBulkPlan,
  type BulkEditPlan, type BulkEditTrade, type BooleanAction,
} from '../../selectors/bulkEdit';

const GRADES = ['A+', 'A', 'B', 'C', 'D', 'F'] as const;

/** ⚠ Same list and same order as the annotation editor. Two surfaces editing one
 *  field must not offer different flags. */
const BREACHES: readonly { key: string; label: string }[] = [
  { key: 'planNotFollowed', label: 'Plan not followed' },
  { key: 'stopNotHonored', label: 'Stop not honored' },
  { key: 'targetNotHonored', label: 'Target not honored' },
  { key: 'revengeTrade', label: 'Revenge trade' },
  { key: 'fomoTrade', label: 'FOMO trade' },
  { key: 'overtraded', label: 'Overtraded' },
  { key: 'subAPlus', label: 'Sub-A+ setup' },
];

export interface BulkEditModalProps {
  /** The selected trades, already read with their editable fields. */
  rows: readonly BulkEditTrade[];
  /** Scope summary for the header — computed by the caller from the table rows. */
  scope: { dateMin: string; dateMax: string; contracts: string[]; accounts: number;
    wins: number; losses: number; totalPnl: number;
    /**
     * ⚠ Counted by the CALLER from the table rows, which already carry
     * `annotated` — that is how the Needs Review split works.
     *
     * The first version hardcoded this to the selection size, so the row read
     * "3 of 3 currently unannotated" on three trades that were all reviewed. A
     * false statement about the user's own data, on the screen whose whole job
     * is to say what you are about to change. Caught by reading the live modal,
     * not by a test.
     */
    unannotated: number };
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onApply: (plan: BulkEditPlan) => void;
  onDelete: () => void;
}

export function BulkEditModal(
  { rows, scope, busy, error, onCancel, onApply, onDelete }: BulkEditModalProps,
) {
  const { patterns } = usePatterns();
  const { exitModels } = useExitModels();
  const { accounts } = useAccounts();
  const { prefs } = usePreferences();
  const [plan, setPlan] = useState<BulkEditPlan>(() => emptyBulkPlan());

  const n = rows.length;
  const set = (fn: (p: BulkEditPlan) => BulkEditPlan) => setPlan((p) => fn({ ...p }));

  // ── The per-row hints: what the SELECTION currently holds. ──
  const held = useMemo(() => {
    const blank = (v: unknown) => !String(v ?? '').trim();
    return {
      untagged: rows.filter((t) => blank(t.patternId)).length,
      noExit: rows.filter((t) => blank(t.exitModelId)).length,
      ungraded: rows.filter((t) => blank(t.grade)).length,
      noMistake: rows.filter((t) => !(t.mistakeTags ?? []).length).length,
      noNote: rows.filter((t) => blank(t.notes)).length,
      noRisk: rows.filter((t) => !(Number(t.riskPoints) > 0)).length,
      unannotated: scope.unannotated,
    };
  }, [rows, n, scope.unannotated]);

  const overwrites = useMemo(() => bulkOverwrites(rows, plan), [rows, plan]);
  const changes = useMemo(
    () => rows.filter((t) => bulkPatchFor(t, plan) !== null).length,
    [rows, plan],
  );
  const nothingOn = bulkPlanIsEmpty(plan);

  const toggle = (key: keyof BulkEditPlan) => () => set((p) => {
    const s = p[key] as { on: boolean };
    return { ...p, [key]: { ...s, on: !s.on } } as BulkEditPlan;
  });

  return (
    <div className="modal-scrim" role="dialog" aria-modal="true" aria-label="Bulk Edit">
      <div className="modal wide be-modal">
        <header className="modal-head">
          <b>Bulk Edit</b>
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>×</button>
        </header>

        <div className="modal-body">
          {/* ── SCOPE ─────────────────────────────────────────────────── */}
          <div className="be-scope">
            <div className="be-scope-head">
              <span className="label">Scope</span>
              <span className="count">{n} trade{n === 1 ? '' : 's'}</span>
            </div>
            <div className="be-scope-summary">
              {scope.dateMin ? (
                <span className="chip">
                  Date {scope.dateMin}{scope.dateMax !== scope.dateMin ? ` – ${scope.dateMax}` : ''}
                </span>
              ) : null}
              <span className="chip">
                {scope.contracts.length <= 5
                  ? (scope.contracts.join(' + ') || '—')
                  : `${scope.contracts.length} contracts`}
              </span>
              {scope.accounts > 1 ? <span className="chip">{scope.accounts} accounts</span> : null}
              <span className="stat">
                <b>{scope.wins}W / {scope.losses}L</b>
                {scope.wins + scope.losses
                  ? <> · <b>{Math.round((scope.wins / (scope.wins + scope.losses)) * 100)}%</b> win rate</>
                  : null}
                {' '}· Net P&amp;L{' '}
                <b className={scope.totalPnl >= 0 ? 'green' : 'red'}>{money(scope.totalPnl, 0)}</b>
              </span>
            </div>
          </div>

          <Group label="Tagging">
            <Row on={plan.pattern.on} onToggle={toggle('pattern')} label="Pattern"
              hint={`${held.untagged} of ${n} currently untagged`}>
              <select
                aria-label="Pattern to apply" value={plan.pattern.value}
                onChange={(e) => set((p) => ({ ...p, pattern: { ...p.pattern, value: e.target.value } }))}
              >
                <option value="">(clear the pattern)</option>
                {(patterns ?? []).map((x) => <option key={x.id} value={String(x.id)}>{x.name}</option>)}
              </select>
              <Preserve
                on={!!plan.pattern.preserve} label="Set only where it is currently blank"
                onToggle={() => set((p) => ({ ...p, pattern: { ...p.pattern, preserve: !p.pattern.preserve } }))}
              />
            </Row>

            {/* ⚠ THIS ROW WAS DISABLED, saying "needs the exit-model catalogue, which
                this client does not load yet". The catalogue shipped with the trade form
                and the copy stayed — §3c, the fifth time that shape has bitten. The
                ENGINE always supported `plan.exitModel`; only the row was withheld. */}
            <Row on={plan.exitModel.on} onToggle={toggle('exitModel')} label="Exit Model"
              hint={`${held.noExit} of ${n} currently without one`}>
              <select
                aria-label="Exit model to apply" value={plan.exitModel.value}
                onChange={(e) => set((p) => ({ ...p, exitModel: { ...p.exitModel, value: e.target.value } }))}
              >
                <option value="">(clear the exit model)</option>
                {(exitModels ?? []).map((x) => <option key={x.id} value={String(x.id)}>{x.name}</option>)}
              </select>
              <Preserve
                on={!!plan.exitModel.preserve} label="Set only where it is currently blank"
                onToggle={() => set((p) => ({ ...p, exitModel: { ...p.exitModel, preserve: !p.exitModel.preserve } }))}
              /></Row>

            <Row on={plan.grade.on} onToggle={toggle('grade')} label="Grade"
              hint={`${held.ungraded} of ${n} currently ungraded`}>
              <select
                aria-label="Grade to apply" value={plan.grade.value}
                onChange={(e) => set((p) => ({ ...p, grade: { ...p.grade, value: e.target.value } }))}
              >
                <option value="">(clear the grade)</option>
                {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
              <Preserve
                on={!!plan.grade.preserve} label="Set only where it is currently blank"
                onToggle={() => set((p) => ({ ...p, grade: { ...p.grade, preserve: !p.grade.preserve } }))}
              />
            </Row>
          </Group>

          <Group label="Confluences">
            <Unavailable label="Confluences"
              why="needs the per-trade confluence registry, which this client does not load yet" />
          </Group>

          <Group label="Mistake tags">
            <Row on={plan.mistakeTags.on} onToggle={toggle('mistakeTags')} label="Mistake tags"
              hint={`${n - held.noMistake} of ${n} have any tag`}>
              <div className="be-tagpick">
                {(prefs?.customMistakeTags ?? []).map((tag) => {
                  const on = plan.mistakeTags.value.includes(tag);
                  return (
                    <label key={tag} className={on ? 'on' : ''}>
                      <input
                        type="checkbox" checked={on}
                        onChange={() => set((p) => ({
                          ...p,
                          mistakeTags: {
                            ...p.mistakeTags,
                            value: on
                              ? p.mistakeTags.value.filter((x) => x !== tag)
                              : [...p.mistakeTags.value, tag],
                          },
                        }))}
                      />{' '}{tag}
                    </label>
                  );
                })}
              </div>
              <Preserve
                on={!!plan.mistakeTags.preserve} label="Add to the existing tags instead of replacing them"
                onToggle={() => set((p) => ({ ...p, mistakeTags: { ...p.mistakeTags, preserve: !p.mistakeTags.preserve } }))}
              />
            </Row>
          </Group>

          <Group label="Booleans">
            <Row on={plan.booleans.on} onToggle={toggle('booleans')} label="Discipline flags"
              hint="add only the breaches you want to change — the rest stay as they are">
              {/* ⚠ THREE STATES per flag, and the third is the default. "Leave"
                  is not a cosmetic option: it is what stops opening this section
                  rewriting every flag on every selected trade. */}
              <div className="be-flags">
                {BREACHES.map((b) => {
                  const cur: BooleanAction = plan.booleans.states[b.key] ?? 'skip';
                  return (
                    <label key={b.key} className="be-flag">
                      <span>{b.label}</span>
                      <select
                        aria-label={b.label} value={cur}
                        onChange={(e) => set((p) => ({
                          ...p,
                          booleans: {
                            ...p.booleans,
                            states: { ...p.booleans.states, [b.key]: e.target.value as BooleanAction },
                          },
                        }))}
                      >
                        <option value="skip">Leave as is</option>
                        <option value="set">Flag it</option>
                        <option value="clear">Clear it</option>
                      </select>
                    </label>
                  );
                })}
              </div>
              <p className="muted">
                Every flag means something went <b>wrong</b>. “Clear it” removes the flag
                entirely — it does not record that the trade went right.
              </p>
            </Row>
          </Group>

          <Group label="Notes">
            <Row on={plan.notes.on} onToggle={toggle('notes')} label="Notes"
              hint={`${n - held.noNote} of ${n} have a note`}>
              <textarea
                aria-label="Notes to apply" rows={3} value={plan.notes.value}
                onChange={(e) => set((p) => ({ ...p, notes: { ...p.notes, value: e.target.value } }))}
              />
              <Preserve
                on={!!plan.notes.preserve} label="Append to the existing note instead of replacing it"
                onToggle={() => set((p) => ({ ...p, notes: { ...p.notes, preserve: !p.notes.preserve } }))}
              />
            </Row>
          </Group>

          <Group label="Risk plan">
            <Row on={plan.riskPoints.on} onToggle={toggle('riskPoints')} label="Risk (pts)"
              hint={`${held.noRisk} of ${n} have no risk plan`}>
              <input
                type="number" step="0.25" min="0" aria-label="Risk in points"
                value={plan.riskPoints.value || ''}
                onChange={(e) => set((p) => ({ ...p, riskPoints: { ...p.riskPoints, value: Number(e.target.value) || 0 } }))}
              />
              <Preserve
                on={!!plan.riskPoints.preserve} label="Set only where it is currently blank"
                onToggle={() => set((p) => ({ ...p, riskPoints: { ...p.riskPoints, preserve: !p.riskPoints.preserve } }))}
              />
            </Row>

            <Row on={plan.rrPlanned.on} onToggle={toggle('rrPlanned')} label="R:R Planned"
              hint="the planned reward-to-risk multiple">
              <input
                type="number" step="0.1" min="0" aria-label="R:R planned"
                value={plan.rrPlanned.value || ''}
                onChange={(e) => set((p) => ({ ...p, rrPlanned: { ...p.rrPlanned, value: Number(e.target.value) || 0 } }))}
              />
              <Preserve
                on={!!plan.rrPlanned.preserve} label="Set only where it is currently blank"
                onToggle={() => set((p) => ({ ...p, rrPlanned: { ...p.rrPlanned, preserve: !p.rrPlanned.preserve } }))}
              />
            </Row>
          </Group>

          <Group label="Account reassignment">
            <Row on={plan.account.on} onToggle={toggle('account')} label="Move trades to a different account"
              hint="rare action · use to fix mis-routed imports">
              <select
                aria-label="Account to move to" value={plan.account.value}
                onChange={(e) => set((p) => ({ ...p, account: { ...p.account, value: e.target.value } }))}
              >
                <option value="">(pick an account)</option>
                {(accounts ?? []).map((a) => (
                  <option key={String(a.id)} value={String(a.id)}>{a.name}</option>
                ))}
              </select>
            </Row>
          </Group>

          <Group label="Annotation">
            <Row on={plan.annotated.on} onToggle={toggle('annotated')} label="Mark these trades as annotated"
              hint={`default ON · removes from Needs Review · ${held.unannotated} of ${n} currently unannotated`}>
              <p className="muted">
                Default ON: bulk-editing <b>is</b> annotating. Trades flip to annotated and
                leave the Needs Review queue. Uncheck only if you want to edit fields
                without flipping the annotated flag (rare).
              </p>
            </Row>
          </Group>

          {/* ── The confirmation, INLINE and before the press. ──────────── */}
          {overwrites.length ? (
            <div className="be-warn" role="alert">
              <b>This will overwrite existing values:</b>
              <ul>
                {overwrites.map((o) => (
                  <li key={o.field}>
                    {o.field} — on {o.count} trade{o.count === 1 ? '' : 's'}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {error ? <p className="red" role="alert">{error}</p> : null}
        </div>

        <footer className="modal-foot">
          <button type="button" className="btn danger" onClick={onDelete} disabled={busy}>
            Delete {n} trade{n === 1 ? '' : 's'}
          </button>
          <span className="grow" />
          {/* ⚠ Says how many trades the plan actually CHANGES, which is not always
              the selection size — preserve modes skip trades that are already
              filled, and a plan that changes nothing must not look like it will. */}
          <span className="muted">
            {nothingOn ? 'Nothing selected to change'
              : `${num(changes, 0)} of ${n} will change`}
          </span>
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            type="button" className="btn primary"
            disabled={busy || nothingOn || changes === 0}
            onClick={() => onApply(plan)}
          >
            {busy ? 'Applying…' : `Apply changes to ${n} trade${n === 1 ? '' : 's'}`}
          </button>
        </footer>
      </div>
    </div>
  );
}

// ── bits ─────────────────────────────────────────────────────────────────────

function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="be-group">
      <h4 className="be-group-label">{label}</h4>
      {children}
    </section>
  );
}

function Row(
  { on, onToggle, label, hint, children }:
  { on: boolean; onToggle: () => void; label: string; hint: string; children: React.ReactNode },
) {
  return (
    <div className={`be-field-row${on ? ' active' : ''}`}>
      <label className="be-field-row-head">
        <input type="checkbox" checked={on} onChange={onToggle} aria-label={label} />
        <span className="be-label">{label}</span>
        <span className="be-hint muted">{hint}</span>
      </label>
      {/* ⚠ The body renders ONLY when the section is on. A visible-but-inert
          input is the thing that makes people believe they set a value. */}
      {on ? <div className="be-body">{children}</div> : null}
    </div>
  );
}

function Preserve({ on, label, onToggle }: { on: boolean; label: string; onToggle: () => void }) {
  return (
    <label className="be-preserve">
      <input type="checkbox" checked={on} onChange={onToggle} />
      <span>{label}</span>
    </label>
  );
}

function Unavailable({ label, why }: { label: string; why: string }) {
  return (
    <div className="be-field-row is-unavailable">
      <label className="be-field-row-head">
        <input type="checkbox" disabled aria-label={`${label} (unavailable)`} />
        <span className="be-label">{label}</span>
        <span className="be-hint muted">not editable here — {why}</span>
      </label>
    </div>
  );
}
